import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GEO_TARGET_CITIES } from '@src/constants/geoTargets'
import { segmentTrack } from '@src/functions/segment'

/** The query param Google Ads puts the geo target criteria ID in. */
const LOCATION_PARAM = 'location'

/**
 * `useLayoutEffect` warns when React renders on the server, so fall back to
 * `useEffect` there. The layout effect never runs server-side anyway.
 */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * The raw `?location=` value, or "" — read straight off the URL, no router.
 *
 * Anything that is not a run of digits is discarded before it is used as a key.
 * Criteria IDs are always numeric, and an unfiltered key would reach
 * `Object.prototype` — `?location=toString` would otherwise "resolve" to a
 * native function and end up interpolated into the hero heading.
 */
function readLocationParam(): string {
  if (typeof window === 'undefined') return ''
  try {
    const raw = new URLSearchParams(window.location.search).get(LOCATION_PARAM) ?? ''
    return /^[0-9]+$/.test(raw) ? raw : ''
  } catch {
    // URLSearchParams does not throw on malformed input, but window.location can
    // be unavailable in odd embedding contexts. Never take the page down for this.
    return ''
  }
}

/** The city for a criteria ID, or null. Own properties only — see above. */
function lookupCity(locationId: string): string | null {
  if (!Object.prototype.hasOwnProperty.call(GEO_TARGET_CITIES, locationId)) return null
  return GEO_TARGET_CITIES[locationId]
}

/** `junk-removal` for `/junk-removal`. Only used as a Segment property. */
function readServiceSlug(): string {
  if (typeof window === 'undefined') return ''
  return window.location.pathname.split('/')[1] ?? ''
}

export type UseLocalizedCityOptions = {
  /**
   * Opts the caller out entirely — no lookup, no analytics, always null. Used by
   * the city landing pages, which are already localized with real per-city
   * regulatory content and must never be relabelled by the param, and by the
   * handful of service pages whose headline already names its own geography.
   */
  disabled?: boolean
}

/**
 * Resolves `?location=<Google Ads geo target criteria ID>` to a city name we
 * have chosen to localize for.
 *
 * Returns null unless the param is present *and* in `GEO_TARGET_CITIES`, so an
 * unknown ID, a region ID or garbage all render the generic page.
 *
 * **Why it reads the URL rather than `router.query`.** Service pages are
 * statically generated, so `router.query` is empty until `router.isReady`, an
 * extra tick after hydration. Parsing `window.location.search` is synchronous
 * and correct on the first client render.
 *
 * **Why a layout effect.** It runs after hydration mutates the DOM but before
 * the browser paints, so the city lands in the same frame. Combined with an
 * additive headline (the city is appended, never swapped in for other copy),
 * nothing incorrect is ever painted and then corrected.
 *
 * Localization is deliberately client-side only: crawlers get the generic copy,
 * which is what the hardcoded canonical URL claims. Do not move this to
 * `getServerSideProps` — it would cost static caching on 45 pages.
 */
export function useLocalizedCity(options?: UseLocalizedCityOptions): string | null {
  const { disabled = false } = options ?? {}
  const [city, setCity] = useState<string | null>(null)
  const trackedRef = useRef(false)

  useIsomorphicLayoutEffect(() => {
    if (disabled) {
      setCity(null)
      return
    }
    const locationId = readLocationParam()
    setCity(locationId ? lookupCity(locationId) : null)
  }, [disabled])

  // Analytics runs in a plain effect so it never sits between hydration and
  // paint. Mirrors the home page's Dynamic Insert events (LandingPage/Pitch)
  // so paid performance is comparable across page types, with the service slug
  // added so we can see which service/city pairs convert — and therefore which
  // cities are worth a real indexable page next.
  //
  // One caveat when reading these: on the home page `Location Not Served` comes
  // from the backend actually saying we do not serve that area. Here it only
  // means the ID was not in GEO_TARGET_CITIES, which is a superset — it also
  // covers cities we serve but have not added, and non-city IDs. A non-numeric
  // param is dropped before this point and fires nothing at all.
  useEffect(() => {
    if (disabled || trackedRef.current) return
    const locationId = readLocationParam()
    if (!locationId) return
    trackedRef.current = true
    const matched = lookupCity(locationId)
    segmentTrack(matched ? 'Location Served' : 'Location Not Served', {
      location: matched ?? '',
      from: 'Location Param',
      service: readServiceSlug(),
      locationId,
    })
  }, [disabled])

  return city
}

export default useLocalizedCity
